import { auditData, performanceData } from './projectData'
import auditEpisodesRaw from './auditEpisodes.json'
import { MarkerGlyph, TaskMarkerIcon } from './TaskMarker'

type AuditEpisode = {
  task: string
  episode: number
  durationGain: number
  coverageGain: number
}

const auditEpisodes = auditEpisodesRaw as AuditEpisode[]
const taskVisuals = new Map(auditData.map((datum) => [datum.task, datum]))

const CHART_MAX = 165
const BAR_TOP = 24
const BAR_BOTTOM = 118

function yForTime(value: number) {
  return BAR_BOTTOM - (value / CHART_MAX) * (BAR_BOTTOM - BAR_TOP)
}

function valueLabelY(errorTop: number) {
  return Math.max(12, errorTop - 6)
}

function parseSuccessCount(value: string) {
  const [success, trials] = value.split('/').map(Number)
  return { success, trials, rate: (success / trials) * 100 }
}

export function TaskPerformanceChart() {
  return (
    <div className="performance-figure" role="group" aria-label="Completion time comparison across six real-robot tasks">
      <div className="chart-legend">
        <span><i className="legend-swatch baseline" />Raw policy</span>
        <span><i className="legend-swatch dats" />DATS</span>
        <span>Mean ± sample SD · successful trials</span>
      </div>
      <div className="performance-grid">
        {performanceData.map((datum) => {
          const visual = taskVisuals.get(datum.task)
          const baselineY = yForTime(datum.baseline)
          const datsY = yForTime(datum.dats)
          const baselineTop = yForTime(datum.baseline + datum.baselineSd)
          const baselineBottom = yForTime(datum.baseline - datum.baselineSd)
          const datsTop = yForTime(datum.dats + datum.datsSd)
          const datsBottom = yForTime(datum.dats - datum.datsSd)
          return (
            <article className="performance-panel" key={datum.task}>
              <h3>
                {visual && <TaskMarkerIcon marker={visual.marker} colour={visual.colour} />}
                {datum.task}
              </h3>
              <svg viewBox="0 0 180 148" aria-label={`${datum.task}: Raw ${datum.baseline} seconds, DATS ${datum.dats} seconds`}>
                {[24, 55.3, 86.7, 118].map((y) => <line className="chart-grid-line" x1="25" x2="166" y1={y} y2={y} key={y} />)}
                <text className="axis-tick" x="10" y="28">165</text>
                <text className="axis-tick" x="10" y="59">110</text>
                <text className="axis-tick" x="10" y="91">55</text>
                <text className="axis-tick" x="10" y="122">0</text>
                <rect className="bar baseline" x="49" y={baselineY} width="32" height={BAR_BOTTOM - baselineY} />
                <rect className="bar dats" x="111" y={datsY} width="32" height={BAR_BOTTOM - datsY} />
                <line className="error-line" x1="65" x2="65" y1={baselineTop} y2={baselineBottom} />
                <line className="error-line" x1="60" x2="70" y1={baselineTop} y2={baselineTop} />
                <line className="error-line" x1="60" x2="70" y1={baselineBottom} y2={baselineBottom} />
                <line className="error-line" x1="127" x2="127" y1={datsTop} y2={datsBottom} />
                <line className="error-line" x1="122" x2="132" y1={datsTop} y2={datsTop} />
                <line className="error-line" x1="122" x2="132" y1={datsBottom} y2={datsBottom} />
                <text className="bar-value" x="65" y={valueLabelY(baselineTop)}>{datum.baseline.toFixed(1)}</text>
                <text className="bar-value" x="127" y={valueLabelY(datsTop)}>{datum.dats.toFixed(1)}</text>
                <text className="bar-label" x="65" y="139">RAW</text>
                <text className="bar-label" x="127" y="139">DATS</text>
              </svg>
            </article>
          )
        })}
      </div>
    </div>
  )
}

export function SuccessRateChart() {
  const rows = performanceData.map((datum) => ({
    ...datum,
    baselineResult: parseSuccessCount(datum.baselineSuccess),
    datsResult: parseSuccessCount(datum.datsSuccess),
  }))
  const baselineTotal = rows.reduce((total, row) => total + row.baselineResult.success, 0)
  const datsTotal = rows.reduce((total, row) => total + row.datsResult.success, 0)
  const totalTrials = rows.reduce((total, row) => total + row.baselineResult.trials, 0)

  return (
    <div className="success-figure" role="group" aria-label={`Observed task success: Raw ${baselineTotal} of ${totalTrials}, DATS ${datsTotal} of ${totalTrials}`}>
      <div className="success-overall">
        <span>All tasks</span>
        <p><i className="legend-swatch baseline" /><strong>{((baselineTotal / totalTrials) * 100).toFixed(1)}%</strong><small>Raw · {baselineTotal}/{totalTrials}</small></p>
        <p><i className="legend-swatch dats" /><strong>{((datsTotal / totalTrials) * 100).toFixed(1)}%</strong><small>DATS · {datsTotal}/{totalTrials}</small></p>
      </div>
      <div className="success-grid">
        {rows.map((datum) => {
          const visual = taskVisuals.get(datum.task)
          return (
            <article className="success-panel" key={datum.task}>
              <h4>{visual && <TaskMarkerIcon marker={visual.marker} colour={visual.colour} />}{datum.task}</h4>
              <div className="success-bar-row">
                <span>Raw</span>
                <i className="success-track"><b className="baseline" style={{ width: `${datum.baselineResult.rate}%` }} /></i>
                <strong>{datum.baselineSuccess} · {datum.baselineResult.rate.toFixed(0)}%</strong>
              </div>
              <div className="success-bar-row">
                <span>DATS</span>
                <i className="success-track"><b className="dats" style={{ width: `${datum.datsResult.rate}%` }} /></i>
                <strong>{datum.datsSuccess} · {datum.datsResult.rate.toFixed(0)}%</strong>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}

const PLOT = { left: 86, right: 940, top: 44, bottom: 430 }
const X_MIN = -5
const X_MAX = 35
const Y_MIN = 0
const Y_MAX = 110

function xScale(value: number) {
  return PLOT.left + ((value - X_MIN) / (X_MAX - X_MIN)) * (PLOT.right - PLOT.left)
}

function yScale(value: number) {
  return PLOT.bottom - ((value - Y_MIN) / (Y_MAX - Y_MIN)) * (PLOT.bottom - PLOT.top)
}

export function AuditChart() {
  const xTicks = [-5, 0, 10, 20, 30]
  const yTicks = [0, 25, 50, 75, 100]

  return (
    <div className="audit-figure">
      <div className="audit-legend" aria-label="Task markers">
        {auditData.map((datum) => <span key={datum.task}><TaskMarkerIcon marker={datum.marker} colour={datum.colour} />{datum.task}</span>)}
      </div>
      <div className="audit-scroll">
        <svg className="audit-chart" viewBox="0 0 1000 500" role="img" aria-labelledby="audit-title audit-desc">
          <title id="audit-title">Paired duration change and co-window gain across six tasks</title>
          <desc id="audit-desc">All 237 processed episodes, with task means and sample standard deviation error bars. Positive horizontal values mean DATS supervision is shorter than Gap. Positive vertical values mean DATS has higher co-window coverage.</desc>
          <defs>
            <pattern id="dot-grid" width="18" height="18" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#d8d5cc" />
            </pattern>
          </defs>
          <rect x="0" y="0" width="1000" height="500" fill="url(#dot-grid)" opacity=".62" />
          <rect className="plot-surface" x={PLOT.left} y={PLOT.top} width={PLOT.right - PLOT.left} height={PLOT.bottom - PLOT.top} />
          {xTicks.map((tick) => (
            <g key={`x-${tick}`}>
              <line className={tick === 0 ? 'zero-line' : 'chart-grid-line'} x1={xScale(tick)} x2={xScale(tick)} y1={PLOT.top} y2={PLOT.bottom} />
              <text className="axis-tick" x={xScale(tick)} y="454">{tick}</text>
            </g>
          ))}
          {yTicks.map((tick) => (
            <g key={`y-${tick}`}>
              <line className="chart-grid-line" x1={PLOT.left} x2={PLOT.right} y1={yScale(tick)} y2={yScale(tick)} />
              <text className="axis-tick" x="67" y={yScale(tick) + 4}>{tick}</text>
            </g>
          ))}
          <text className="axis-label" x="513" y="486">PAIRED DURATION CHANGE · GAP − DATS (S)</text>
          <text className="axis-label" transform="translate(20 300) rotate(-90)">CO-WINDOW COVERAGE GAIN · DATS − GAP (PP)</text>
          <g className="episode-points" aria-label="Individual processed episodes">
            {auditEpisodes.map((episode) => {
              const visual = taskVisuals.get(episode.task)
              if (!visual) return null
              return (
                <g key={`${episode.task}-${episode.episode}`} transform={`translate(${xScale(episode.durationGain)} ${yScale(episode.coverageGain)})`}>
                  <title>{`${episode.task}, episode ${episode.episode}: ${episode.durationGain.toFixed(2)} s; ${episode.coverageGain.toFixed(2)} pp`}</title>
                  <MarkerGlyph marker={visual.marker} size={7.2} fill={visual.colour} stroke="#20211d" strokeWidth={0.45} className="episode-marker" />
                </g>
              )
            })}
          </g>
          {auditData.map((datum) => {
            const x = xScale(datum.durationGain)
            const y = yScale(datum.coverageGain)
            const x1 = xScale(datum.durationGain - datum.durationSd)
            const x2 = xScale(datum.durationGain + datum.durationSd)
            const y1 = yScale(datum.coverageGain + datum.coverageSd)
            const y2 = yScale(datum.coverageGain - datum.coverageSd)
            return (
              <g className="audit-mark" key={datum.task}>
                <title>{`${datum.task}: ${datum.durationGain.toFixed(1)} ± ${datum.durationSd.toFixed(1)} s; ${datum.coverageGain.toFixed(1)} ± ${datum.coverageSd.toFixed(1)} pp`}</title>
                <line className="audit-error" x1={x1} x2={x2} y1={y} y2={y} />
                <line className="audit-error" x1={x} x2={x} y1={y1} y2={y2} />
                <line className="audit-cap" x1={x1} x2={x1} y1={y - 6} y2={y + 6} />
                <line className="audit-cap" x1={x2} x2={x2} y1={y - 6} y2={y + 6} />
                <line className="audit-cap" x1={x - 6} x2={x + 6} y1={y1} y2={y1} />
                <line className="audit-cap" x1={x - 6} x2={x + 6} y1={y2} y2={y2} />
                <g transform={`translate(${x} ${y})`}>
                  <MarkerGlyph marker={datum.marker} size={20} fill={datum.colour} stroke="#20211d" strokeWidth={2} className="mean-marker" />
                </g>
              </g>
            )
          })}
        </svg>
      </div>
      <p className="chart-note">Light points show individual demonstrations; outlined markers show task means ± sample SD.</p>
    </div>
  )
}
